import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, MagnifyingGlass, FolderOpen, MapPin,
  Funnel, ArrowsClockwise, Warning, FolderSimple,
  Receipt, CurrencyDollar, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import NewProjectModal from "../components/projects/NewProjectModal";
import { getProjects } from "../api/projects";
import { getStatusConfig, isCompleted } from "../data/mockProjects";
import { useFinancialsVisible } from "../hooks/useFinancialsVisible";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrencyCompact(n) {
  if (n == null || n === "") return null;
  const num = typeof n === "number" ? n : parseFloat(n);
  if (!Number.isFinite(num)) return null;
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD",
    notation: num >= 10000 ? "compact" : "standard",
    maximumFractionDigits: num >= 10000 ? 1 : 0,
  }).format(num);
}

function StatusBadge({ status }) {
  const cfg = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project, onOpen, financialsHidden }) {
  const primaryClient = project.clients?.[0] || "—";
  const extraClients  = (project.clients?.length || 0) - 1;
  const quoted        = fmtCurrencyCompact(project.quoted_price);
  const invoiced      = fmtCurrencyCompact(project.final_invoice_amount);
  const masked        = <span className="tracking-wider select-none">••••</span>;

  return (
    <article
      className="bg-white border border-slate-200 p-5 flex flex-col gap-3 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer group"
      data-testid={`project-card-${project.id}`}
      onClick={() => onOpen(project)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-cabinet text-sm font-bold text-slate-900 tracking-tight leading-snug group-hover:text-primary transition-colors">
            {project.name}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {primaryClient}
            {extraClients > 0 && <span className="text-slate-400"> +{extraClients} more</span>}
          </p>
        </div>
        <StatusBadge status={project.status} />
      </div>

      {project.description && (
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{project.description}</p>
      )}

      {(quoted || invoiced) && (
        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
          {quoted && (
            <div className="flex items-center gap-1.5" data-testid={`card-quoted-${project.id}`}>
              <CurrencyDollar size={12} className="text-slate-400" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Quoted</span>
              <span className="text-xs font-bold text-slate-700">{financialsHidden ? masked : quoted}</span>
            </div>
          )}
          {invoiced && (
            <div className="flex items-center gap-1.5" data-testid={`card-invoiced-${project.id}`}>
              <Receipt size={12} className="text-emerald-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Invoiced</span>
              <span className="text-xs font-bold text-emerald-700">{financialsHidden ? masked : invoiced}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400 mt-auto pt-2 border-t border-slate-100">
        {(() => { const locs = Array.isArray(project.project_location) ? project.project_location : (project.project_location ? [project.project_location] : []); return locs.length > 0 ? <span className="flex items-center gap-1"><MapPin size={11} />{locs.join(", ")}</span> : null; })()}
        {project.invoice_number && (
          <span className="flex items-center gap-1 ml-auto"><Receipt size={11} />{project.invoice_number}</span>
        )}
        {project.onedrive_folder_url && (
          <FolderSimple size={11} className="text-[#0078D4] ml-auto" title="OneDrive folder linked" />
        )}
      </div>
    </article>
  );
}

// ─── Stats ────────────────────────────────────────────────────────────────────

function StatsBar({ projects }) {
  const stats = [
    { label: "Total",     value: projects.length, sub: "all time",   testId: "stat-total" },
    { label: "Active",    value: projects.filter((p) => p.status === "active").length, sub: "in progress", testId: "stat-active", color: "text-green-700" },
    { label: "On Hold",   value: projects.filter((p) => p.status === "on-hold").length, sub: "paused", testId: "stat-on-hold", color: "text-yellow-700" },
    { label: "Completed", value: projects.filter((p) => isCompleted(p.status)).length, sub: "delivered", testId: "stat-completed", color: "text-slate-500" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((s) => (
        <div key={s.label} data-testid={s.testId} className="bg-white border border-slate-200 px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
          <p className={`font-cabinet text-3xl font-black tracking-tight mt-1 ${s.color || "text-slate-900"}`}>{s.value}</p>
          <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showNewModal, setShow]   = useState(false);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatus] = useState("all");
  const { hidden: financialsHidden, toggle: toggleFinancials } = useFinancialsVisible();

  const loadProjects = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await getProjects();
      setProjects(data);
    } catch {
      setError("Failed to load projects. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleSaveNew = (saved) => {
    setProjects((prev) => [saved, ...prev]);
    setShow(false);
    // Jump straight to the new project's detail page.
    navigate(`/projects/${saved.id}`);
  };

  const handleOpen = useCallback((project) => {
    navigate(`/projects/${project.id}`);
  }, [navigate]);

  const filtered = useMemo(() => {
    let list = projects;
    if (statusFilter !== "all") list = list.filter((p) => p.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.clients?.some((c) => c.toLowerCase().includes(q)) ||
        p.client_emails?.some((e) => e.toLowerCase().includes(q)) ||
        (Array.isArray(p.project_location) ? p.project_location.join(" ") : p.project_location ?? "").toLowerCase().includes(q) ||
        p.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [projects, search, statusFilter]);

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="projects-page">
      <div className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">Projects</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage your engineering projects and drawing packages.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={toggleFinancials}
              className="text-slate-600"
              title={financialsHidden ? "Show financial amounts" : "Hide financial amounts"}
              data-testid="toggle-financials-page"
            >
              {financialsHidden
                ? <><EyeSlash size={13} className="mr-1.5" weight="bold" /> Show $</>
                : <><Eye      size={13} className="mr-1.5" weight="bold" /> Hide $</>}
            </Button>
            <Button onClick={() => setShow(true)} data-testid="new-project-btn" className="bg-primary hover:bg-primary/90 text-white">
              <Plus size={15} className="mr-1.5" weight="bold" /> New Project
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <ArrowsClockwise size={22} className="text-primary animate-spin" />
            <p className="text-sm text-slate-500">Loading projects…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <Warning size={26} className="text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button size="sm" variant="outline" onClick={loadProjects}>Retry</Button>
          </div>
        ) : (
          <>
            <StatsBar projects={projects} />

            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="projects-search"
                  placeholder="Search name, client, location, tag…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Funnel size={13} className="text-slate-400" />
                {["all", "active", "on-hold", "ready for invoice", "completed: invoice sent", "completed: invoice paid", "completed", "draft"].map((s) => (
                  <button key={s} data-testid={`filter-${s}`} onClick={() => setStatus(s)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-colors capitalize ${
                      statusFilter === s ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}>
                    {s === "all" ? "All" : s.replace("-", " ")}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="projects-empty">
                <div className="w-14 h-14 bg-slate-100 flex items-center justify-center mb-4">
                  <FolderOpen size={28} className="text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-600">No projects found</p>
                <p className="text-xs text-slate-400 mt-1">
                  {search ? "Try a different search term." : "Create your first project to get started."}
                </p>
                {!search && (
                  <Button size="sm" onClick={() => setShow(true)} className="mt-4 bg-primary text-white" data-testid="empty-new-project-btn">
                    <Plus size={13} className="mr-1.5" /> New Project
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-grid">
                {filtered.map((p) => (
                  <ProjectCard key={p.id} project={p} onOpen={handleOpen} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showNewModal && <NewProjectModal onClose={() => setShow(false)} onSave={handleSaveNew} />}
    </div>
  );
}
